import type { ColorAttribute } from './helpers';

import { casingStrategy } from './variantMappingRepairStrategies/casing';
import { shopifyOptionOrderStrategy, shopifyOptionPositionsStrategy } from './variantMappingRepairStrategies/shopify';
import type { RepairContext, RepairStrategyID, StoreOption, VariantMappingRepairStrategy } from './variantMappingRepairStrategies/types';

// Resolve Shopify reordering and position shifts before applying the platform-independent casing repair.
const repairStrategies: readonly VariantMappingRepairStrategy[] = [shopifyOptionOrderStrategy, shopifyOptionPositionsStrategy, casingStrategy];

export const getApplicableRepairStrategyIDs = (context: RepairContext) =>
    repairStrategies.filter((strategy) => strategy.isApplicable(context)).map((strategy) => strategy.id);

export type RepairStep = { strategyID: RepairStrategyID; before: ColorAttribute[]; after: ColorAttribute[] };

export type RepairEntry = { key: string; variantGuid: string; name: string; code: string };
export type VariantMappingRepair = RepairEntry & {
    before: ColorAttribute[];
    after: ColorAttribute[] | null;
    reason: 'unmatched' | 'duplicate' | 'invalid' | null;
    steps: RepairStep[];
};
export type ChannelMappingRepair = {
    salesChannelID: string;
    label: string;
    isMain: boolean;
    productSKU: string;
    context: RepairContext;
    repairs: VariantMappingRepair[];
    availableStrategies: RepairStrategyID[];
};

type ParsedEntry = { entry: RepairEntry; attributes: ColorAttribute[] | null };
type StrategyResult = { after: ColorAttribute[] | null; steps: RepairStep[] };

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const normalizeID = (id: unknown): string | null => (typeof id === 'string' ? id : typeof id === 'number' && Number.isFinite(id) ? String(id) : null);

const normalizeAttribute = (attribute: unknown): ColorAttribute | null => {
    if (!isRecord(attribute) || !isRecord(attribute.Value) || typeof attribute.Label !== 'string' || typeof attribute.Value.Label !== 'string') return null;
    const id = normalizeID(attribute.Id);
    const valueID = normalizeID(attribute.Value.Id);
    // Keep legacy numeric IDs and extra fields without mutating the saved mapping.
    return id === null || valueID === null
        ? null
        : {
              ...attribute,
              Id: id,
              Label: attribute.Label,
              Value: { ...attribute.Value, Id: valueID, Label: attribute.Value.Label }
          };
};

const normalizeAttributes = (attributes: unknown): ColorAttribute[] | null => {
    if (!Array.isArray(attributes)) return null;
    const normalized = attributes.map(normalizeAttribute);
    return normalized.every((attribute) => attribute !== null) ? normalized : null;
};

function readAttributes(code: string): ColorAttribute[] | null {
    try {
        return normalizeAttributes(JSON.parse(code || '[]'));
    } catch {
        return null;
    }
}

const hasUniqueOptions = (attributes: readonly ColorAttribute[]) => new Set(attributes.map((attribute) => attribute.Id)).size === attributes.length;
const sameAttributes = (before: readonly ColorAttribute[], after: readonly ColorAttribute[]) => JSON.stringify(before) === JSON.stringify(after);
const combinationKey = (attributes: readonly ColorAttribute[]) =>
    JSON.stringify(attributes.map((attribute) => [attribute.Id, attribute.Value.Id]).sort(([a], [b]) => a.localeCompare(b)));

const matchesStore = (attributes: readonly ColorAttribute[], options: readonly StoreOption[]) =>
    hasUniqueOptions(attributes) &&
    attributes.every((attribute) => {
        const matches = options.filter((option) => option.optionID === attribute.Id && option.label === attribute.Label);
        return matches.length === 1 && matches[0].values.some((value) => value.valueID === attribute.Value.Id && value.label === attribute.Value.Label);
    });

/** Revalidate at the apply boundary: a failed/invalid plan must never become a saved null mapping. */
export function getApplicableVariantMappingRepairs(repairs: readonly VariantMappingRepair[]): (VariantMappingRepair & { after: ColorAttribute[] })[] {
    return repairs.flatMap((repair) => {
        if (repair.reason !== null) return [];
        const after = normalizeAttributes(repair.after);
        return after?.length && hasUniqueOptions(after) ? [{ ...repair, after }] : [];
    });
}

const runStrategies = (attributes: ColorAttribute[], context: RepairContext, strategies: readonly VariantMappingRepairStrategy[]): StrategyResult =>
    strategies.reduce<StrategyResult>(
        (result, strategy) => {
            if (result.after === null) return result;
            const after = strategy.repair(result.after, context);
            if (after === null) return { after: null, steps: [] };
            return {
                after,
                steps: sameAttributes(result.after, after) ? result.steps : [...result.steps, { strategyID: strategy.id, before: result.after, after }]
            };
        },
        { after: attributes, steps: [] }
    );

const buildEntryRepair = (
    { entry, attributes }: ParsedEntry,
    context: RepairContext,
    strategies: readonly VariantMappingRepairStrategy[]
): VariantMappingRepair[] => {
    if (attributes === null) return [{ ...entry, before: [], after: null, reason: 'invalid', steps: [] }];
    if (!attributes.length || matchesStore(attributes, context.options)) return [];
    const result = runStrategies(attributes, context, strategies);
    if (!result.after?.length || !matchesStore(result.after, context.options))
        return [{ ...entry, before: attributes, after: null, reason: 'unmatched', steps: [] }];
    return sameAttributes(attributes, result.after) ? [] : [{ ...entry, before: attributes, ...result, reason: null }];
};

const rejectDuplicatePass = (entries: readonly ParsedEntry[], repairs: readonly VariantMappingRepair[]): VariantMappingRepair[] => {
    const repairsByKey = new Map(repairs.map((repair) => [repair.key, repair]));
    const combinations = entries
        .flatMap(({ entry, attributes }) => {
            const proposed = repairsByKey.get(entry.key)?.after ?? attributes;
            return proposed?.length ? [combinationKey(proposed)] : [];
        })
        .sort();
    const duplicates = new Set(combinations.filter((key, index) => index > 0 && key === combinations[index - 1]));
    return repairs.map((repair) =>
        repair.after && duplicates.has(combinationKey(repair.after)) ? { ...repair, after: null, reason: 'duplicate', steps: [] } : repair
    );
};

export const hasVariantMappingRepairCollisions = (entries: readonly RepairEntry[], repairs: readonly VariantMappingRepair[]) => {
    const parsed = entries.map((entry) => ({ entry, attributes: readAttributes(entry.code) }));
    return rejectDuplicatePass(parsed, repairs).some((repair, index) => repair !== repairs[index]);
};

const rejectDuplicateRepairs = (entries: readonly ParsedEntry[], repairs: VariantMappingRepair[]): VariantMappingRepair[] => {
    // Rejected repairs restore their original combination, which can expose further collisions.
    // Each pass returns new rejected entries; retain iteration here to avoid recursive stack growth.
    let current = repairs;
    while (true) {
        const next = rejectDuplicatePass(entries, current);
        if (next.every((repair, index) => repair === current[index])) return current;
        current = next;
    }
};

/** Parse, compose applicable strategies, then reject collisions without mutating inputs or repair results. */
export function buildVariantMappingRepairs(entries: readonly RepairEntry[], options: readonly StoreOption[], isShopify: boolean): VariantMappingRepair[] {
    const context = { options, isShopify };
    const strategies = repairStrategies.filter((strategy) => strategy.isApplicable(context));
    const parsed = entries.map((entry) => ({ entry, attributes: readAttributes(entry.code) }));
    const repairs = parsed.flatMap((entry) => buildEntryRepair(entry, context, strategies));
    return rejectDuplicateRepairs(parsed, repairs);
}
